// src/components/resumo/annotations/HighlightedContent.tsx
import React, { useMemo, useEffect, useRef } from 'react';
import { Highlight } from './annotations.types';
import { renderHTMLWithHighlights } from './textHighlighter';
import { reconcileHighlights } from './blockAnchor';

interface HighlightedContentProps {
    html: string;
    highlights: Highlight[];
    pageId: string;
    darkMode?: boolean;
    onReconciled?: (pageId: string, highlights: Highlight[]) => void;
}

const HighlightedContent: React.FC<HighlightedContentProps> = ({
    html,
    highlights,
    pageId,
    darkMode = false,
    onReconciled,
}) => {
    const pageHighlights = useMemo(() =>
        highlights.filter(h => h.pageId === pageId),
        [highlights, pageId]
    );

    const { content, reconciledHighlights, changed } = useMemo(() => {
        const { highlights: reconciled, changed } = reconcileHighlights(pageHighlights, html);
        return {
            content: renderHTMLWithHighlights(html, reconciled),
            reconciledHighlights: reconciled,
            changed,
        };
    }, [html, pageHighlights]);

    // Fire onReconciled once per pageId when positions were corrected
    const firedRef = useRef<string | null>(null);
    const onReconciledRef = useRef(onReconciled);
    useEffect(() => { onReconciledRef.current = onReconciled; }, [onReconciled]);

    useEffect(() => {
        if (changed && firedRef.current !== pageId && onReconciledRef.current) {
            firedRef.current = pageId;
            onReconciledRef.current(pageId, reconciledHighlights);
        }
    }, [changed, pageId, reconciledHighlights]);

    return (
        <div
            className={`rrp-rendered-content ${darkMode ? 'dark' : ''}`}
            style={{ userSelect: 'text' }}
        >
            {content}
        </div>
    );
};

export default HighlightedContent;
